import React, { FC, useMemo, useState } from "react";
import { clsx } from "clsx";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useStyles } from "../styles";
import { ReturnCart } from "../../../gql/__generated__/schema";
import { prepareReturnCartCostFees } from "../hooks/useTransformToNth";
import useConsumerSettings from "../../hooks/useConsumerSettings";
import { Currency, ExtendedFee } from "@narvar/nth-kit-returns-headless";
import { getTranslation } from "../../../shared/modules/config";

interface CostItemProps {
  label: string;
  fee: ExtendedFee;
  isStrong?: boolean;
  isGreen?: boolean;
}

export const CostItem: FC<CostItemProps> = ({
  label,
  fee,
  isStrong = false,
  isGreen = false,
}) => {
  const { localeFull, includeCurrency } = useConsumerSettings();
  const styles = useStyles();

  const classes = clsx({
    [styles.cartSummaryItemWrapper]: true,
    [styles.textBold]: isStrong,
    [styles.textGreen]: isGreen,
  });
  return (
    <div className={classes}>
      <span>{label}</span>
      <span>
        <Currency
          locale={localeFull}
          includeCurrency={includeCurrency}
          currency={fee.amount.currency}
          value={fee.amount.value}
        />
      </span>
    </div>
  );
};

interface ReturnCreditsSummaryProps {
  returnCartSummary?: ReturnCart;
}

const ReturnCreditsSummary: FC<ReturnCreditsSummaryProps> = ({
  returnCartSummary,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { localeFull, includeCurrency } = useConsumerSettings();
  const styles = useStyles();

  const fees = useMemo(() => {
    if (!returnCartSummary) {
      return null;
    }
    return prepareReturnCartCostFees(returnCartSummary);
  }, [returnCartSummary]);

  // The total amount is negative because it represents a refund
  const totalAmount = (returnCartSummary?.cartSummary.total.amount ?? 0) * -1;
  const totalCurrency = returnCartSummary?.cartSummary.total.currencyCode;

  return (
    <Accordion
      className={styles.accordion}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}>
      <AccordionSummary
        className={styles.accordionSummary}
        classes={{
          content: styles.accordionSummaryContent,
          expandIcon: styles.iconButton,
        }}
        expandIcon={<ExpandMoreIcon />}>
        <Typography className={styles.panelHeader}>
          <span>
            {getTranslation("shop_now_checkout_return_credits_title")}
          </span>
          <Currency
            locale={localeFull}
            includeCurrency={includeCurrency}
            currency={totalCurrency}
            value={totalAmount}
          />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <ul className={styles.ul}>
          {fees?.map((fee, index) => (
            <li key={index}>
              <CostItem label={fee.title ?? ""} fee={fee} />
            </li>
          ))}
        </ul>
      </AccordionDetails>
    </Accordion>
  );
};

export default ReturnCreditsSummary;
